// MeteringPage (C-METER) — mirrors review/commerce-app/metering.jsx.
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/pages/MeteringPage.tsx, and moved off its fixtures
// onto GET /portal/api/metering/. Every departure from the drop is marked
// ADAPTED and says why; the rest is the handoff as delivered, so a reissued
// prototype can be re-ported rather than merged.
//
// ADAPTED (throughout): design-system components are imported by module path
// rather than from the `@miq/design-system` barrel — AppShell.tsx explains why —
// and button handlers are `onPress`, the React Aria event the DS Button is built
// on.
//
// ADAPTED (formatting): every figure on this page arrives raw — quantities as
// exact decimal strings, moments as ISO-8601 — and is formatted here. The
// quantities are never parsed as numbers on the way to the screen; see
// data/format.ts for why that matters on a page somebody reconciles an invoice
// against.
//
// ADAPTED (no money): the drop's fourth KPI is "Metered charges · MTD" and its
// dimension table has "Rate / unit" and "Charge · MTD" columns. This service's
// ledger holds no prices — what a unit costs lives in the agreement the sales
// channel holds, which is also what issues the invoice — so those three figures
// have no honest source here. Multiplying a quantity by a rate read from a
// listing constant would be this portal inventing a second, unauthoritative
// bill. What replaces them is what the ledger DOES hold and what a customer
// reconciling a marketplace invoice actually needs: the exact quantity
// recorded, the quantity a channel has settled, and the difference between the
// two, which is where whole-unit flooring and carried remainders live. Same
// reasoning as the server-rendered billing history (D-7).
//
// ADAPTED (no "Decision pack" column): the drop's dimension table names the
// pack each dimension belongs to. Nothing in this service records a
// dimension-to-pack mapping, and the column is dropped rather than guessed at.
//
// ADAPTED (a month picker, not the drop's segment): the drop offers a "This
// month / Last month / 6 months" segment. The window is always ONE month —
// the totals are cut on a month and the chart draws six of them — so
// "6 months" would name a window nothing on the page is computed over. What
// it became is a month per tab, over the months the endpoint says it will
// answer for, captioned with the window the totals were actually cut on so
// the label cannot drift from the arithmetic.
//
// ADAPTED (no "Export usage" button): there is no export endpoint. A button
// that does nothing on a live page is the thing a customer reports as broken.
//
// The dimension segment above the chart IS live: dimensions are separate
// charts rather than layers of one, because volume and well-days cannot be
// summed into a total in any unit.

import { useCallback, useState } from 'react';
import {
  Check,
  Clock,
  Gauge,
  Hourglass,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@miq/design-system/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@miq/design-system/components/ui/tabs';

import { UsageStackedBars } from '../components/charts/UsageChart';
import { ScreenFailed, ScreenLoading } from '../components/ScreenState';
import { MonthPicker } from '../components/MonthPicker';
import {
  ABSENT,
  countText,
  monthLabel,
  monthOf,
  percentText,
  quantityText,
  relativeTime,
  usageHourLabel,
} from '../data/format';
import {
  readMetering,
  readMeteringFor,
  type MeteringRecord,
  type MeteringScreen,
  type UsageDimensionChart,
} from '../data/reads';
import { usePortalRead } from '../data/usePortalRead';
import type { AuthorizedFetch } from '../auth/api';
import { useDocumentTitle } from '../../lib/documentTitle';

const TITLE = 'Metering';

/**
 * How each submission outcome reads, and which badge carries it.
 *
 * The core's own vocabulary, translated here rather than on the server: what an
 * outcome MEANS to a reader is this screen's business, and a label invented in
 * the endpoint would be a second vocabulary to keep in step with the first.
 *
 * The empty string is the outcome of a submission written before the adapter
 * was called and never answered — a row to investigate, which is why it reads
 * as awaiting rather than as failed.
 */
const OUTCOME: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  accepted: { variant: 'default', label: 'Accepted' },
  already_recorded: { variant: 'secondary', label: 'Already recorded' },
  nothing_to_submit: { variant: 'secondary', label: 'Nothing to submit' },
  withheld: { variant: 'secondary', label: 'Withheld' },
  rejected: { variant: 'destructive', label: 'Rejected' },
 '': { variant: 'outline', label: 'Awaiting answer' },
};

function outcomeOf(record: MeteringRecord) {
  return OUTCOME[record.outcome] ?? { variant: 'outline' as const, label: record.outcome };
}

function Kpi({
  label,
  icon: KIcon,
  value,
  sub,
  iconClass = 'text-muted-foreground',
}: {
  label: string;
  icon: LucideIcon;
  value: React.ReactNode;
  sub: string;
  iconClass?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
          {label}
          <KIcon className={`size-3.5 ${iconClass}`} />
        </div>
        <div className="mt-1.5 text-2xl font-semibold tracking-tight">{value}</div>
        <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
      </CardContent>
    </Card>
  );
}

export function MeteringPage() {
  useDocumentTitle(TITLE);
  // `null` means "did not ask", which the service reads as the current month.
  // Kept as the absence rather than as today's date so the screen does not
  // pin itself to a month at mount and then disagree with the service about
  // which month is current when a request crosses midnight on the 1st.
  const [month, setMonth] = useState<string | null>(null);

  // Memoised per month because `usePortalRead` holds this in a dependency
  // array: an inline arrow would re-run the request on every render and never
  // settle. Changing the month is exactly when we DO want it to re-run.
  const read = useCallback(
    (authorizedFetch: AuthorizedFetch) =>
      month === null
        ? readMetering(authorizedFetch)
        : readMeteringFor(authorizedFetch, month),
    [month],
  );
  const screen = usePortalRead(read);

  if (screen.status === 'failed') {
    return <ScreenFailed title={TITLE} message={screen.message} />;
  }
  if (screen.status === 'loading') {
    return <ScreenLoading title={TITLE} />;
  }

  return (
    <Metering
      screen={screen.data}
      month={month}
      onMonth={setMonth}
    />
  );
}

function Metering({
  screen,
  month,
  onMonth,
}: {
  screen: MeteringScreen;
  month: string | null;
  onMonth: (month: string | null) => void;
}) {
  const { summary, dimensions, records, period, usage_by_month: chart } = screen;

  return (
    <div className="space-y-5 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Commerce · Metering
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Metering</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Decision packs meter one or more dimensions — a business unit with a
            price — as (dimension, quantity) records, reported hourly per
            instance to the sales channel that bills them.
          </p>
        </div>
        <MonthPicker
          months={screen.selectable_periods}
          selected={month}
          onSelect={onMonth}
          period={period}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Dimensions reporting"
          icon={Gauge}
          value={countText(summary.dimensions_reporting)}
          sub={`${countText(summary.hours_recorded_this_month)} hours recorded in ${monthOf(period)}`}
        />
        <Kpi
          label="Records answered"
          icon={Check}
          iconClass="text-success"
          value={percentText(summary.records_resolved_this_month, summary.records_this_month)}
          sub={`${countText(summary.records_resolved_this_month)} of ${countText(summary.records_this_month)} submissions in ${monthOf(period)}`}
        />
        <Kpi
          label="Last report"
          icon={Clock}
          value={<span className="text-xl">{relativeTime(summary.latest_submission_at)}</span>}
          sub={
            summary.latest_submission_at === null
              ? 'nothing has been handed over yet'
              : usageHourLabel(summary.latest_submission_at)
          }
        />
        <Kpi
          label="Awaiting an answer"
          icon={Hourglass}
          value={countText(summary.records_awaiting_answer)}
          sub="submissions a channel has not yet answered for"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Usage by dimension · {monthOf(period)}
          </CardTitle>
          <CardDescription>
            What the ledger recorded exactly, and what a sales channel holds.
            The difference is whole-unit flooring and hours still in flight — it
            rides forward rather than being lost. Prices and invoices live with
            the channel that issues them.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {dimensions.length === 0 ? (
            <Empty>
              No usage recorded in {monthOf(period)}. Metering begins with an instance's
              first usage report.
            </Empty>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dimension</TableHead>
                    <TableHead className="text-right">Instances</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Recorded</TableHead>
                    <TableHead className="text-right">Settled by channel</TableHead>
                    <TableHead className="text-right">Carried forward</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dimensions.map((row) => (
                    <TableRow key={row.dimension}>
                      <TableCell className="font-mono text-xs">{row.dimension}</TableCell>
                      <TableCell className="text-right">
                        {countText(row.instances_reporting)}
                      </TableCell>
                      <TableCell className="text-right">
                        {countText(row.hours_recorded)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {quantityText(row.quantity_recorded)}
                      </TableCell>
                      <TableCell className="text-right">
                        {quantityText(row.quantity_settled)}
                        {/* Withheld usage was never sent to the channel, so it is
                            stated beside the settled figure rather than in it. */}
                        {row.quantity_withheld !== null ? (
                          <div className="text-xs text-muted-foreground">
                            {quantityText(row.quantity_withheld)} withheld
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right">
                        {quantityText(row.carry)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <UsageByInstance chart={chart} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent metering records</CardTitle>
          <CardDescription>
            Hourly submissions to the sales channel. A channel that already holds
            a record says so rather than billing it twice, so a retried hour is
            normal and never double-charged.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {records.length === 0 ? (
            <Empty>Nothing has been handed to a sales channel yet.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usage hour</TableHead>
                    <TableHead>Instance</TableHead>
                    <TableHead>Dimension</TableHead>
                    <TableHead className="text-right">Recorded</TableHead>
                    <TableHead className="text-right">Submitted</TableHead>
                    <TableHead>Channel record</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => {
                    const status = outcomeOf(record);
                    return (
                      <TableRow key={record.public_id}>
                        <TableCell className="text-muted-foreground">
                          {usageHourLabel(record.usage_hour)}
                        </TableCell>
                        <TableCell className="font-medium">{record.instance}</TableCell>
                        <TableCell className="font-mono text-xs">{record.dimension}</TableCell>
                        <TableCell className="text-right font-medium">
                          {quantityText(record.quantity_recorded)}
                        </TableCell>
                        <TableCell className="text-right">
                          {quantityText(record.quantity_submitted)}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {record.channel_record_ref || ABSENT}
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The six-month chart, and the dimension it is drawn for.
 *
 * One dimension at a time, selected by the segment the drop puts in this card's
 * header. Stacking them together would add a volume to a count of days.
 *
 * Three shapes, because a segmented control needs something to choose between.
 * With two or more dimensions the DS Tabs wrap the whole card so that the tab
 * list in the header and the chart in the content are one tab set — a tab list
 * with no panel to control would leave `aria-controls` pointing at nothing.
 * With one dimension there is nothing to choose, so the dimension is named in
 * the caption instead; with none, the chart says so itself.
 */
function UsageByInstance({ chart }: { chart: MeteringScreen['usage_by_month'] }) {
  const months = chart.months.map(monthLabel);
  const [selected, setSelected] = useState(chart.dimensions[0]?.dimension ?? '');
  const span = `${months[0]} – ${months[months.length - 1]}`;

  if (chart.dimensions.length < 2) {
    const only = chart.dimensions[0];
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Usage by instance</CardTitle>
          <CardDescription>
            {span} · monthly totals, stacked by instance
            {only ? ` · ${only.dimension}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Chart months={months} dimension={only} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Tabs value={selected} onValueChange={setSelected}>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Usage by instance</CardTitle>
            <CardDescription>
              {span} · monthly totals, stacked by instance
            </CardDescription>
          </div>
          <TabsList>
            {chart.dimensions.map((row) => (
              <TabsTrigger key={row.dimension} value={row.dimension}>
                {row.dimension}
              </TabsTrigger>
            ))}
          </TabsList>
        </CardHeader>
        <CardContent>
          {chart.dimensions.map((row) => (
            <TabsContent key={row.dimension} value={row.dimension}>
              <Chart months={months} dimension={row} />
            </TabsContent>
          ))}
        </CardContent>
      </Card>
    </Tabs>
  );
}

function Chart({
  months,
  dimension,
}: {
  months: string[];
  dimension: UsageDimensionChart | undefined;
}) {
  return (
    <UsageStackedBars
      height={230}
      months={months}
      series={(dimension?.series ?? []).map((series) => ({
        name: series.instance,
        quantities: series.quantities,
      }))}
    />
  );
}

/** A table that has nothing in it, saying so. Zero rows is a state. */
function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-6 py-8 text-sm text-muted-foreground">{children}</p>;
}

